import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/input";
import { SkeletonRows } from "@/components/ui/skeleton";
import { Table, TBody, Td, Th, THead, Tr } from "@/components/ui/table";

const ENTITIES = ["", "Patient", "Appointment", "EyeRecord", "Prescription", "Document", "User", "ClinicConfig"];

const actionTone: Record<string, "active" | "pending" | "cancelled" | "neutral"> = {
  CREATE: "active",
  UPDATE: "pending",
  DELETE: "cancelled",
  ARCHIVE: "cancelled",
  CANCEL: "cancelled",
  RESTORE: "active",
  LOGIN: "neutral",
};

export function AuditPage() {
  const [entity, setEntity] = useState("");
  const [page, setPage] = useState(1);

  const audit = useQuery({
    queryKey: ["audit", entity, page],
    queryFn: () => api.audit.list({ entity: entity || undefined, page, pageSize: 25 }),
  });

  const totalPages = audit.data ? Math.max(1, Math.ceil(audit.data.total / audit.data.pageSize)) : 1;

  return (
    <div>
      <PageHeader
        title="Audit log"
        subtitle="Every create, update, delete and cancellation — who, what, when"
        actions={
          <Select
            className="w-44"
            value={entity}
            onChange={(e) => {
              setEntity(e.target.value);
              setPage(1);
            }}
          >
            {ENTITIES.map((e) => (
              <option key={e} value={e}>
                {e || "All entities"}
              </option>
            ))}
          </Select>
        }
      />

      {audit.isLoading ? (
        <SkeletonRows rows={8} />
      ) : (
        <>
          <Table>
            <THead>
              <Tr>
                <Th>When</Th>
                <Th>User</Th>
                <Th>Action</Th>
                <Th>Entity</Th>
                <Th>Changes</Th>
              </Tr>
            </THead>
            <TBody>
              {(audit.data?.items ?? []).map((entry) => (
                <Tr key={entry.id}>
                  <Td className="whitespace-nowrap text-xs text-text-secondary">
                    {format(new Date(entry.createdAt), "d MMM yyyy, HH:mm:ss")}
                  </Td>
                  <Td className="whitespace-nowrap">{entry.user?.name}</Td>
                  <Td>
                    <Badge tone={actionTone[entry.action] ?? "neutral"}>{entry.action}</Badge>
                  </Td>
                  <Td>
                    {entry.entity}{" "}
                    <span className="font-mono text-xs text-text-tertiary">
                      {entry.entityId.slice(-8)}
                    </span>
                  </Td>
                  <Td>
                    {entry.diff ? (
                      <pre className="max-w-md overflow-x-auto whitespace-pre-wrap break-all font-mono text-xs text-text-secondary">
                        {JSON.stringify(entry.diff, null, 0)}
                      </pre>
                    ) : (
                      <span className="text-text-tertiary">—</span>
                    )}
                  </Td>
                </Tr>
              ))}
              {(audit.data?.items ?? []).length === 0 && (
                <Tr>
                  <Td colSpan={5} className="py-10 text-center text-text-tertiary">
                    No audit entries.
                  </Td>
                </Tr>
              )}
            </TBody>
          </Table>

          <div className="mt-4 flex items-center justify-between text-sm text-text-secondary">
            <span>
              {audit.data?.total ?? 0} entries · page {page} of {totalPages}
            </span>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                Previous
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
